import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../../../Avatar';
import type { Communication } from '../../../../services/communications';

interface BreadcrumbCrumb {
    label: string;
    path: string | null;
}

function Breadcrumb({
    crumbs,
    onNavigate
}: {
    crumbs: BreadcrumbCrumb[];
    onNavigate: (path: string) => void;
}) {
    return (
        <div className="flex items-center gap-1.5 text-[13px] text-gray-500 min-w-0">
            {crumbs.map((crumb, i) => (
                <span key={crumb.label} className="flex items-center gap-1.5 min-w-0">
                    {i > 0 && <ChevronRightIcon className="h-3 w-3 text-gray-300 shrink-0" />}
                    {crumb.path ? (
                        <button
                            type="button"
                            onClick={() => onNavigate(crumb.path as string)}
                            className="hover:text-gray-800 transition-colors truncate"
                        >
                            {crumb.label}
                        </button>
                    ) : (
                        <span className="font-semibold text-gray-700 truncate">{crumb.label}</span>
                    )}
                </span>
            ))}
        </div>
    );
}

interface CommunicationDetailHeaderProps {
    communication: Communication;
    practiceName: string | null;
    onNavigate: (path: string) => void;
}

// The thread detail page's header: a pure breadcrumb bar now. Title,
// reference, status, assignee, and delete all live elsewhere (identity
// card, meta rail, timeline heading).
export function CommunicationDetailHeader({
    communication,
    practiceName,
    onNavigate
}: CommunicationDetailHeaderProps) {
    const crumbs: BreadcrumbCrumb[] =
        communication.practiceId && practiceName
            ? [
                  { label: 'Accounts', path: '/accounts/practices' },
                  { label: 'Practices', path: '/accounts/practices' },
                  { label: practiceName, path: `/accounts/practices/${communication.practiceId}` },
                  {
                      label: 'Communications',
                      path: `/accounts/practices/${communication.practiceId}?tab=communications`
                  },
                  { label: communication.referenceNo, path: null }
              ]
            : [
                  { label: 'Accounts', path: '/accounts/communications' },
                  { label: 'Communications', path: '/accounts/communications' },
                  { label: communication.referenceNo, path: null }
              ];

    return (
        <div className="flex items-center justify-between gap-4 px-8 pt-4 pb-2 shrink-0">
            <Breadcrumb crumbs={crumbs} onNavigate={onNavigate} />
            <div className="w-10 h-10 shrink-0">
                <Avatar />
            </div>
        </div>
    );
}
